// Ops #315: the form-side rules for an Initiative subscription plan. They mirror the server's checks so a mistake shows
// next to the field before saving; the server still validates everything and has the last word.

export const MODELS = {
  DARE_COUNT: { label: 'By number of DAREs', help: 'Tiers by how many DAREs the integrator runs.', unit: 'DAREs' },
  USER_COUNT: {
    label: 'By number of users',
    help: 'Tiers by agents and farmers under all of its DAREs.',
    unit: 'agents + farmers',
  },
  ENTERPRISE: { label: 'Enterprise', help: 'One agreed price, or "Contact us" for a custom quote.', unit: '' },
};

export const PAYMENT_PROVIDERS = [
  { value: 'RAZORPAY', label: 'Razorpay' },
  { value: 'STRIPE', label: 'Stripe' },
  { value: 'MANUAL', label: 'Manual (cash / bank transfer)' },
];

export const MAX_UNITS = 100000000;
export const MAX_PRICE = 100000000;

const isWhole = (text) => /^\d+$/.test(String(text ?? '').trim());
const isMoney = (text) => /^\d+(\.\d{1,2})?$/.test(String(text ?? '').trim());
const toNumber = (text) => Number(String(text).trim());

export const MAX_FEATURE_LENGTH = 255;

export const emptyTier = () => ({ label: '', maxUnits: '', open: false, monthly: '', yearly: '', features: [] });

/** A new draft: one tier, the last (open) one, so the first thing the admin sees is a valid shape to fill in. */
export const emptyDraft = () => ({
  initiativeId: '',
  countryId: '',
  name: '',
  description: '',
  pricingModel: 'DARE_COUNT',
  enterpriseMode: 'PRICED',
  contactNote: '',
  paymentProvider: 'RAZORPAY',
  tiers: [{ ...emptyTier(), open: true }],
});

/** The lower limit of each tier: 1, then the previous upper limit + 1. Null once a limit is missing or wrong. */
export const minUnitsOf = (tiers) => {
  const mins = [];
  let next = 1;
  tiers.forEach((tier) => {
    mins.push(next);
    next = next !== null && !tier.open && isWhole(tier.maxUnits) ? toNumber(tier.maxUnits) + 1 : null;
  });
  return mins;
};

/** `{ errors: { 'tiers.0.monthly': message, ... }, ok }`; keys are what the form puts next to the field. */
export const validateDraft = (draft) => {
  const errors = {};
  const need = (key, message) => {
    if (!errors[key]) errors[key] = message;
  };
  if (!draft.initiativeId) need('initiativeId', 'Choose an Initiative');
  if (!draft.countryId) need('countryId', 'Choose a country');
  if (!draft.name.trim()) need('name', 'Enter a plan name');
  if (draft.name.length > 255) need('name', 'Use at most 255 characters');
  if ((draft.description || '').length > 500) need('description', 'Use at most 500 characters');
  if (!draft.paymentProvider) need('paymentProvider', 'Choose a payment provider');

  if (draft.pricingModel === 'ENTERPRISE' && draft.enterpriseMode === 'CONTACT_US') {
    if ((draft.contactNote || '').length > 500) need('contactNote', 'Use at most 500 characters');
    return { errors, ok: Object.keys(errors).length === 0 };
  }

  const enterprise = draft.pricingModel === 'ENTERPRISE';
  const tiers = enterprise ? draft.tiers.slice(0, 1) : draft.tiers;
  if (!tiers.length) need('tiers', 'Add at least one tier');
  let previousMonthly = null;
  let previousYearly = null;
  let expectedMin = 1;
  tiers.forEach((tier, i) => {
    const at = (field) => `tiers.${i}.${field}`;
    const open = enterprise || tier.open;
    if (!open) {
      if (!isWhole(tier.maxUnits)) {
        need(at('maxUnits'), 'Enter a whole number');
      } else if (toNumber(tier.maxUnits) < expectedMin) {
        need(at('maxUnits'), `Must be ${expectedMin} or more`);
      } else if (toNumber(tier.maxUnits) > MAX_UNITS) {
        need(at('maxUnits'), 'That is too large');
      } else {
        expectedMin = toNumber(tier.maxUnits) + 1;
      }
    } else if (i !== tiers.length - 1) {
      need(at('maxUnits'), 'Only the last tier can have no upper limit');
    }
    const monthly = String(tier.monthly ?? '').trim();
    const yearly = String(tier.yearly ?? '').trim();
    if (!monthly && !yearly) need(at('monthly'), 'Enter a monthly price, a yearly price or both');
    [['monthly', monthly], ['yearly', yearly]].forEach(([field, value]) => {
      if (!value) return;
      if (!isMoney(value)) need(at(field), 'Enter an amount with at most 2 decimals');
      else if (toNumber(value) <= 0) need(at(field), 'Must be more than 0');
      else if (toNumber(value) > MAX_PRICE) need(at(field), 'That is too large');
    });
    (tier.features || []).forEach((feature, f) => {
      if (feature.length > MAX_FEATURE_LENGTH) need(at(`features.${f}`), `Use at most ${MAX_FEATURE_LENGTH} characters`);
    });
    const m = monthly && isMoney(monthly) ? toNumber(monthly) : null;
    const y = yearly && isMoney(yearly) ? toNumber(yearly) : null;
    if (m !== null) {
      if (previousMonthly !== null && m < previousMonthly) need(at('monthly'), 'Lower than the previous tier');
      previousMonthly = m;
    }
    if (y !== null) {
      if (previousYearly !== null && y < previousYearly) need(at('yearly'), 'Lower than the previous tier');
      previousYearly = y;
    }
  });
  return { errors, ok: Object.keys(errors).length === 0 };
};

/** The request body the server expects. Call only on a draft that passed {@link validateDraft}. */
export const toRequest = (draft) => {
  const enterprise = draft.pricingModel === 'ENTERPRISE';
  const contactUs = enterprise && draft.enterpriseMode === 'CONTACT_US';
  const source = contactUs ? [] : enterprise ? draft.tiers.slice(0, 1) : draft.tiers;
  const mins = minUnitsOf(source.map((t) => (enterprise ? { ...t, open: true } : t)));
  const money = (text) => (String(text ?? '').trim() === '' ? null : String(text).trim());
  return {
    initiativeId: draft.initiativeId,
    countryId: draft.countryId,
    name: draft.name.trim(),
    description: draft.description.trim() || null,
    pricingModel: draft.pricingModel,
    enterpriseMode: enterprise ? draft.enterpriseMode : null,
    contactNote: contactUs ? draft.contactNote.trim() || null : null,
    paymentProvider: draft.paymentProvider,
    tiers: source.map((tier, i) => ({
      label: tier.label.trim() || null,
      minUnits: mins[i],
      maxUnits: enterprise || tier.open ? null : toNumber(tier.maxUnits),
      monthlyPrice: money(tier.monthly),
      yearlyPrice: money(tier.yearly),
      features: (tier.features || []).map((f) => f.trim()).filter(Boolean),
    })),
  };
};

/** A plan from the server back into an editable draft. */
export const fromPlan = (plan) => ({
  initiativeId: plan.initiativeId,
  countryId: plan.countryId,
  name: plan.name || '',
  description: plan.description || '',
  pricingModel: plan.pricingModel,
  enterpriseMode: plan.enterpriseMode || 'PRICED',
  contactNote: plan.contactNote || '',
  paymentProvider: plan.paymentProvider,
  tiers: plan.tiers?.length
    ? plan.tiers.map((tier) => ({
      label: tier.label || '',
      maxUnits: tier.maxUnits == null ? '' : String(tier.maxUnits),
      open: tier.maxUnits == null,
      monthly: tier.monthlyPrice == null ? '' : String(tier.monthlyPrice),
      yearly: tier.yearlyPrice == null ? '' : String(tier.yearlyPrice),
      features: tier.features || [],
    }))
    : [{ ...emptyTier(), open: true }],
});

/** "1 to 5 DAREs", "6 and above". */
export const describeRange = (min, max, unit) => {
  const suffix = unit ? ` ${unit}` : '';
  if (max == null) return `${min.toLocaleString('en-US')} and above${suffix}`;
  if (min === max) return `${min.toLocaleString('en-US')}${suffix}`;
  return `${min.toLocaleString('en-US')} to ${max.toLocaleString('en-US')}${suffix}`;
};

/** Money in the plan's own currency, e.g. ₹2,999.00 or $29.00; falls back to "29.00 XYZ" for an unknown code. */
export const formatMoney = (amount, currency) => {
  if (amount === null || amount === undefined || amount === '') return '—';
  const value = Number(amount);
  if (Number.isNaN(value)) return '—';
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(value);
  } catch {
    return `${value.toFixed(2)} ${currency || ''}`.trim();
  }
};

/** One line for the list: how the plan is priced. */
export const summarisePlan = (plan) => {
  if (plan.pricingModel === 'ENTERPRISE') {
    if (plan.enterpriseMode === 'CONTACT_US') return 'Contact us';
    const tier = plan.tiers?.[0];
    return tier ? `Custom price ${formatMoney(tier.monthlyPrice ?? tier.yearlyPrice, plan.currency)}` : '—';
  }
  const count = plan.tiers?.length || 0;
  return `${count} ${count === 1 ? 'tier' : 'tiers'}`;
};
